import { useState } from 'react'
import { Camera } from 'lucide-react'
import { apiClient } from '../api/client'
import { sanitizeUserFacingMessage } from '../utils/userMessage'

export interface UploadedProfilePhoto {
  id: string
  url: string
  thumbUrl: string
}

interface PhotoUploaderProps {
  onPhotoAdded: (photo: UploadedProfilePhoto) => void
  isLoading?: boolean
}

function errText(err: unknown): string {
  if (err instanceof Error) return err.message
  return String(err)
}

/** Короткие подсказки вместо сырого текста Nest/AWS. */
function friendlyUploadError(err: unknown): string {
  const raw = errText(err)
  if (/invalid photo url/i.test(raw)) {
    return 'Не удалось принять это фото. Попробуй JPEG, PNG или WebP поменьше.'
  }
  if (/unsupported file type|invalid file type|unsupported media type/i.test(raw)) {
    return 'Формат не поддерживается. Сохрани снимок как JPEG, PNG или WebP и попробуй снова.'
  }
  if (/file is required|no file|multipart/i.test(raw)) {
    return 'Файл не дошёл до сервера. Проверь соединение и попробуй ещё раз.'
  }
  if (/payload too large|file too large|entity too large|request entity too large/i.test(raw)) {
    return 'Файл слишком большой. Попробуй фото поменьше (до ~12 МБ).'
  }
  if (/network|fetch failed|failed to fetch|load failed/i.test(raw)) {
    return 'Не удаётся связаться с сервером. Проверь интернет и попробуй снова.'
  }
  return sanitizeUserFacingMessage(raw)
}

const INLINE_FALLBACK_MAX_FILE_BYTES = 6 * 1024 * 1024

function fileToDataUrl(file: File): Promise<string> {
  return new Promise((resolve, reject) => {
    const reader = new FileReader()
    reader.onload = () => resolve(String(reader.result || ''))
    reader.onerror = () => reject(new Error('Не удалось прочитать файл'))
    reader.readAsDataURL(file)
  })
}

function shouldSkipInlineFallback(err: unknown): boolean {
  const code = (err as { statusCode?: number }).statusCode
  if (code === 401 || code === 403) return true
  return /failed to fetch|networkerror|load failed/i.test(errText(err))
}

export function PhotoUploader({ onPhotoAdded, isLoading = false }: PhotoUploaderProps) {
  const [uploading, setUploading] = useState(false)
  const [uploadError, setUploadError] = useState<string | null>(null)

  const handlePhotoSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0]
    e.target.value = ''
    if (!file) return

    try {
      setUploading(true)
      setUploadError(null)

      const applyUploaded = (uploaded: { id: string; url: string; thumbUrl?: string }) => {
        if (!uploaded?.id || !uploaded?.url) {
          throw new Error('Некорректный ответ сервера')
        }
        onPhotoAdded({
          id: uploaded.id,
          url: uploaded.url,
          thumbUrl: uploaded.thumbUrl || uploaded.url,
        })
      }

      try {
        const form = new FormData()
        form.append('file', file)
        const uploaded = await apiClient.postForm<{
          id: string
          url: string
          thumbUrl?: string
        }>('/api/photos/upload', form)
        applyUploaded(uploaded)
        return
      } catch (multipartErr) {
        console.warn('[PhotoUploader] multipart upload failed, trying inline fallback', multipartErr)
        if (shouldSkipInlineFallback(multipartErr)) {
          throw multipartErr
        }
        if (file.size > INLINE_FALLBACK_MAX_FILE_BYTES) {
          throw multipartErr
        }
        const dataUrl = await fileToDataUrl(file)
        const uploaded = await apiClient.post<{
          id: string
          url: string
          thumbUrl?: string
        }>('/api/photos', { url: dataUrl })
        applyUploaded(uploaded)
      }
    } catch (err) {
      console.error('Photo upload failed:', err)
      setUploadError(friendlyUploadError(err))
    } finally {
      setUploading(false)
    }
  }

  return (
    <div className="w-full">
      <label className="flex items-center justify-center w-full p-8 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded-lg cursor-pointer hover:border-accent dark:hover:border-pink-400/80 bg-gray-50/50 dark:bg-gray-900/40 transition-colors touch-manipulation">
        <input
          type="file"
          accept="image/*"
          onChange={handlePhotoSelect}
          disabled={uploading || isLoading}
          className="hidden"
        />
        <div className="text-center">
          <Camera className="w-12 h-12 mx-auto mb-2 text-gray-400 dark:text-gray-500" />
          <p className="text-gray-700 dark:text-gray-200">
            {uploading ? 'Загружаем...' : 'Нажми или перетащи фото'}
          </p>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-2">JPEG, PNG или WebP (до ~12 МБ)</p>
        </div>
      </label>
      {uploadError ? (
        <p
          className="mt-3 text-sm text-red-600 dark:text-red-400 whitespace-pre-wrap break-words"
          aria-live="polite"
        >
          {uploadError}
        </p>
      ) : null}
    </div>
  )
}
